'use client';

import { useId, type ReactNode } from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { Icon } from '@/hen-ui/atomics/Icon';
import { Progress } from '@/hen-ui/atomics/Progress';
import { Spinner } from '@/hen-ui/atomics/Spinner';
import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Card } from '@/hen-ui/composites/Card';
import { Flex } from '@/hen-ui/composites/Flex';
import { Modal } from '@/hen-ui/composites/Modal';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/hen-ui/composites/Table';
import { ToggleGroup, ToggleGroupItem } from '@/hen-ui/composites/ToggleGroup';

export type ExportDialogPhase = 'confirm' | 'exporting' | 'error';

export type ExportDialogProgressIndicator = 'bar' | 'spinner';

export type ExportDialogFormat = {
  id: string;
  label: string;
  /**
   * Unavailable formats stay visible and disabled, with `reason` as their accessible
   * description. Consumers pass the full format set and let the dialog present availability
   * rather than filtering unavailable entries out of `formats`.
   */
  available: boolean;
  reason?: string;
};

export type ExportDialogFilterRow = {
  key: string;
  label: string;
  value: string;
};

export type ExportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  title: string;
  description: string;
  filterRows: readonly ExportDialogFilterRow[];
  scopeLabel: string;
  formats: readonly ExportDialogFormat[];
  selectedFormatId: string;
  onSelectFormat?: (id: string) => void;
  formatHint?: string;
  phase: ExportDialogPhase;
  exportingDetail: string;
  /**
   * How the exporting phase shows progress. `bar` renders a progress bar and
   * percentage from `progressPercent`; `spinner` renders an indeterminate
   * spinner for exports that cannot report progress, and ignores
   * `progressPercent`.
   */
  progressIndicator?: ExportDialogProgressIndicator;
  progressPercent?: number;
  errorMessage: string | null;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onRetry: () => void;
};

const INDETERMINATE_PROGRESS_PERCENT = 12;

export function ExportDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  filterRows,
  scopeLabel,
  formats,
  selectedFormatId,
  onSelectFormat,
  formatHint,
  phase,
  exportingDetail,
  progressIndicator = 'bar',
  progressPercent = INDETERMINATE_PROGRESS_PERCENT,
  errorMessage,
  confirmDisabled = false,
  onCancel,
  onConfirm,
  onRetry,
}: ExportDialogProps) {
  const formatReasonId = useId();
  const selectedFormat = formats.find(
    (format) => format.id === selectedFormatId,
  );
  const selectedLabel = selectedFormat?.label ?? selectedFormatId;
  const clampedPercent = Math.min(
    100,
    Math.max(0, Math.round(progressPercent)),
  );
  const exportingLabel = `Preparing ${selectedLabel} export`;
  const canConfirm = selectedFormat?.available === true && !confirmDisabled;

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>
      {open ? (
        <Modal.Content size='md' data-hen-id={COMPONENT_IDS.ExportDialog}>
          {phase === 'confirm' ? (
            <>
              <Modal.Header>
                <Modal.Title>{title}</Modal.Title>
                <Modal.Description>{description}</Modal.Description>
              </Modal.Header>
              <Card padding='lg' rounded='md' trackable={false}>
                <Flex direction='column' gap='lg' width='full'>
                  <Flex direction='column' gap='xs' width='full'>
                    <Text variant='l1' weight='bold' color='subtext'>
                      ACTIVE FILTERS
                    </Text>
                    {filterRows.length > 0 ? (
                      <Card
                        border='strokeLight'
                        padding='none'
                        rounded='md'
                        trackable={false}
                      >
                        <Table noWrapper>
                          <TableBody>
                            {filterRows.map((row, index) => (
                              <TableRow
                                key={row.key}
                                className={
                                  index % 2 === 0 ? 'bg-neutral-50' : undefined
                                }
                              >
                                <TableCell>
                                  <Text variant='l1' color='subtext'>
                                    {row.label}
                                  </Text>
                                </TableCell>
                                <TableCell>
                                  <Text variant='l1'>{row.value}</Text>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Card>
                    ) : (
                      <Text variant='l1' color='subtext'>
                        No active filters
                      </Text>
                    )}
                  </Flex>
                  <Text variant='l1' color='subtext'>
                    {scopeLabel}
                  </Text>
                  <Flex direction='column' gap='xs' width='full'>
                    <Text variant='l1' weight='bold' color='subtext'>
                      EXPORT FORMAT
                    </Text>
                    <Card
                      variant='background-gray'
                      padding='none'
                      rounded='md'
                      trackable={false}
                    >
                      <Flex
                        asChild
                        direction='row'
                        gap='xxs'
                        width='full'
                        childrenFill
                      >
                        <ToggleGroup
                          type='single'
                          value={selectedFormatId}
                          variant='blackFill'
                          wrap={false}
                          onValueChange={(id) => {
                            if (id) onSelectFormat?.(id);
                          }}
                        >
                          {formats.map((format, index) => {
                            const reasonId = format.reason
                              ? `${formatReasonId}-${index}`
                              : undefined;

                            return (
                              <ToggleGroupItem
                                key={format.id}
                                value={format.id}
                                disabled={!format.available}
                                aria-label={format.label}
                                aria-describedby={reasonId}
                                title={format.reason}
                              >
                                {format.label}
                                {format.reason ? (
                                  <Text id={reasonId} tag='span' visuallyHidden>
                                    {format.reason}
                                  </Text>
                                ) : null}
                              </ToggleGroupItem>
                            );
                          })}
                        </ToggleGroup>
                      </Flex>
                    </Card>
                    {formatHint ? (
                      <Text variant='l1' color='subtext'>
                        {formatHint}
                      </Text>
                    ) : null}
                  </Flex>
                </Flex>
              </Card>
              <Modal.Footer>
                <Flex direction='row' gap='xs' justify='center' width='full'>
                  <Button
                    type='button'
                    variant='grayOutline'
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    variant='primaryFill'
                    disabled={!canConfirm}
                    onClick={() => {
                      if (!canConfirm) return;
                      onConfirm();
                    }}
                  >
                    Export
                  </Button>
                </Flex>
              </Modal.Footer>
            </>
          ) : phase === 'exporting' ? (
            <>
              <Modal.Header>
                <Modal.Title>Exporting...</Modal.Title>
                <Text asChild visuallyHidden>
                  <Modal.Description>{exportingLabel}</Modal.Description>
                </Text>
              </Modal.Header>
              <Card padding='lg' rounded='md' trackable={false}>
                <Flex direction='column' align='center' gap='sm' width='full'>
                  {progressIndicator === 'spinner' ? (
                    <Spinner
                      className='size-12 text-neutral-900'
                      label={exportingLabel}
                    />
                  ) : (
                    <>
                      <Progress
                        value={clampedPercent}
                        aria-label={exportingLabel}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={clampedPercent}
                      />
                      <Text variant='b3' weight='bold'>
                        {clampedPercent}%
                      </Text>
                    </>
                  )}
                  <Text variant='b5' align='center'>
                    {exportingDetail}
                  </Text>
                  <Text variant='b5' color='subtext' align='center'>
                    This may take a moment.
                  </Text>
                </Flex>
              </Card>
              <Modal.Footer>
                <Flex width='full' childrenFill>
                  <Button type='button' variant='primaryFill' disabled>
                    Exporting...
                  </Button>
                </Flex>
              </Modal.Footer>
            </>
          ) : (
            <>
              <Modal.Header>
                <Text asChild color='error' weight='bold'>
                  <Modal.Title>Export failed</Modal.Title>
                </Text>
                <Text asChild visuallyHidden>
                  <Modal.Description>
                    The export could not be generated.
                  </Modal.Description>
                </Text>
              </Modal.Header>
              <Card padding='lg' rounded='md' trackable={false}>
                <Flex direction='column' align='center' gap='sm' width='full'>
                  <Icon
                    icon='circle-alert'
                    size='lg'
                    color='error'
                    aria-hidden='true'
                  />
                  <Text variant='b5' align='center' role='alert'>
                    {errorMessage ||
                      "We couldn't generate your file. Your dashboard data was not affected."}
                  </Text>
                </Flex>
              </Card>
              <Modal.Footer>
                <Flex direction='row' gap='xs' width='full' childrenFill>
                  <Button
                    type='button'
                    variant='grayOutline'
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='button'
                    variant='errorOutline'
                    disabled={!canConfirm}
                    onClick={() => {
                      if (!canConfirm) return;
                      onRetry();
                    }}
                  >
                    Try Again
                  </Button>
                </Flex>
              </Modal.Footer>
            </>
          )}
        </Modal.Content>
      ) : null}
    </Modal>
  );
}
